import { MetricName } from '@canvas/frontend-metrics';

import { defineMetric, type MetricMetadataBase } from '../define-metric';
import type { FrameState } from '../stabilized-scheduler';

export const DATABASE_CELL_CONTEXT_MENU_OPEN_ATTR =
  'data-database-cell-context-menu-open';

/**
 * Probes for the presence of the cell context menu's "open" marker at document level.
 *
 * The cell context menu is rendered into a portal (outside the layout table), so the
 * probe queries the whole document rather than scoping to a `tableRoot`. Both the
 * new (ECS effect, `ContextMenuPortalRenderer`) and old (`ContextualMenu`) renderers
 * stamp the same `DATABASE_CELL_CONTEXT_MENU_OPEN_ATTR` plus `data-entry-id` /
 * `data-field-id` on the menu's outermost element when the value menu is visible.
 *
 * Matching by `entryId` / `fieldId` ensures the menu corresponds to the cell that
 * triggered the right-click (defends against unrelated row/column menus that share
 * the `data-testid="database-context-menu"` test id).
 */
export function getCellContextMenuOpenFrameState(
  entryId: string,
  fieldId: string
): FrameState {
  const el = document.querySelector(
    `[${DATABASE_CELL_CONTEXT_MENU_OPEN_ATTR}][data-entry-id="${entryId}"][data-field-id="${fieldId}"]`
  );

  if (!el) {
    return { done: false, stabilityKey: null };
  }

  return { done: true, stabilityKey: `${entryId}:${fieldId}` };
}

export interface CellContextMenuProbeData {
  entryId: string;
  fieldId: string;
}

export interface CellContextMenuMetadata extends MetricMetadataBase {
  databasesSelectionsPerformanceCohort: 'control' | 'test';
}

export const cellContextMenuMetric = defineMetric<
  CellContextMenuProbeData,
  CellContextMenuMetadata
>({
  metricName: MetricName.DATABASE_CELL_CONTEXT_MENU_OPEN_INTERACTION_TIME,
  buildProbe: (probeData) => {
    const { entryId, fieldId } = probeData;

    return () => getCellContextMenuOpenFrameState(entryId, fieldId);
  },
  buildMetadata: (metadata) => {
    return {
      interactionTimingSource: 'dom-cell-context-menu-open',
      databasesSelectionsPerformanceCohort: metadata.databasesSelectionsPerformanceCohort,
    };
  },
});
